import React, { useState } from "react";
import { apiClient } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Upload, ScanEye, Loader2 } from "lucide-react";
import { AiMarkdown } from "@/pages/DailyReports";

export default function PhotoInspector() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState("");
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please upload an image"); return; }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const analyze = async () => {
    if (!file) { toast.error("Choose an image first"); return; }
    setBusy(true); setResult("");
    try {
      const b64 = preview.split(",")[1];
      const { data } = await apiClient().post("/ai/inspect", { image_base64: b64, filename: file.name });
      setResult(data.result);
      toast.success("Analysis complete");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 fade-in-up" data-testid="inspector-page">
      <div>
        <h1 className="font-display text-5xl mt-1">AI Photo Inspector</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Upload any site photo. Get hazard, PPE, defect and progress signals with confidence scoring.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border border-border bg-card p-5">
          <label htmlFor="site-photo"
            className="block border-2 border-dashed border-border p-8 text-center cursor-pointer hover:bg-muted/30 transition-colors">
            {preview ? (
              <img src={preview} alt="preview" className="max-h-72 mx-auto border border-border" />
            ) : (
              <>
                <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                <div className="mt-3 text-sm">Drop or click to upload a site photo</div>
                <div className="mono text-xs text-muted-foreground mt-1">.jpg .png .webp</div>
              </>
            )}
            <input id="site-photo" data-testid="inspector-file" type="file" accept="image/*" onChange={onFile} className="hidden" />
          </label>
          <button onClick={analyze} disabled={!file || busy} data-testid="inspector-analyze-btn"
            className="btn-sharp w-full mt-4 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 font-medium disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanEye className="h-4 w-4" />}
            {busy ? "Analyzing…" : "Analyze photo"}
          </button>
        </div>

        <div className="border border-border bg-card p-5">
          <div className="font-heading text-lg mb-3">Inspection Result</div>
          {result ? (
            <AiMarkdown text={result} testid="inspector-result" />
          ) : (
            <div className="text-sm text-muted-foreground">Awaiting analysis. Results include hazards, PPE compliance, defects, and recommended actions.</div>
          )}
        </div>
      </div>
    </div>
  );
}
